import { CLIP_PLACEMENT_OVERLAY, VIDEO_EDITOR_TRACK_TYPE } from "./constants";
import {
  createDefaultImageClipLayout,
  createFullCanvasClipLayout,
  hasSourceDimensions,
  isFullCanvasClipLayout,
  isValidClipLayout,
  repairLayoutAspectRatio,
} from "./clipLayout";

export const DEFAULT_CLIP_VOLUME = 1;
export const CLIP_LAYOUT_MODE = {
  FULL: "full",
  OVERLAY: "overlay",
};

export const clipHasVolume = (clip) => {
  if (clip?.type === VIDEO_EDITOR_TRACK_TYPE.AUDIO) {
    return true;
  }

  if (clip?.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO) {
    return clip.hasAudio !== false;
  }

  return false;
};

export const clipHasCanvasLayout = (clip) =>
  clip?.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO ||
  clip?.type === VIDEO_EDITOR_TRACK_TYPE.IMAGE;

export const clampClipVolume = (value) => {
  const volume = Number(value);
  if (!Number.isFinite(volume)) {
    return DEFAULT_CLIP_VOLUME;
  }

  return Math.min(1, Math.max(0, volume));
};

export const getClipVolume = (clip) => {
  if (!clipHasVolume(clip) || clip?.volume == null) {
    return DEFAULT_CLIP_VOLUME;
  }

  return clampClipVolume(clip.volume);
};

export const createClipLayoutForType = ({
  type,
  layoutMode = CLIP_LAYOUT_MODE.OVERLAY,
  sourceWidth,
  sourceHeight,
  compositionWidth,
  compositionHeight,
} = {}) => {
  if (type !== VIDEO_EDITOR_TRACK_TYPE.VIDEO && type !== VIDEO_EDITOR_TRACK_TYPE.IMAGE) {
    return undefined;
  }

  if (layoutMode === CLIP_LAYOUT_MODE.FULL) {
    return createFullCanvasClipLayout();
  }

  return createDefaultImageClipLayout({
    sourceWidth,
    sourceHeight,
    compositionWidth,
    compositionHeight,
  });
};

export const hydrateClipDefaults = ({
  clip,
  compositionWidth,
  compositionHeight,
}) => {
  if (!clip) {
    return clip;
  }

  const nextClip = { ...clip };

  if (clipHasVolume(nextClip) && nextClip.volume == null) {
    nextClip.volume = DEFAULT_CLIP_VOLUME;
  }

  if (clipHasVolume(nextClip) && nextClip.volume != null) {
    nextClip.volume = clampClipVolume(nextClip.volume);
  }

  if (clipHasCanvasLayout(nextClip)) {
    if (!isValidClipLayout(nextClip.layout)) {
      nextClip.layout =
        nextClip.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO
          ? createFullCanvasClipLayout()
          : createDefaultImageClipLayout({
              sourceWidth: nextClip.sourceWidth,
              sourceHeight: nextClip.sourceHeight,
              compositionWidth,
              compositionHeight,
            });
    } else if (
      hasSourceDimensions(nextClip) &&
      !isFullCanvasClipLayout(nextClip.layout)
    ) {
      nextClip.layout = repairLayoutAspectRatio({
        layout: nextClip.layout,
        sourceWidth: nextClip.sourceWidth,
        sourceHeight: nextClip.sourceHeight,
        compositionWidth,
        compositionHeight,
      });
    }

    nextClip.placement = CLIP_PLACEMENT_OVERLAY;

    if (!Number.isInteger(nextClip.layerIndex)) {
      nextClip.layerIndex = 0;
    }
  }

  return nextClip;
};

export const hydrateEdlClipDefaults = ({ edl }) => {
  if (!edl?.clips?.byId) {
    return edl;
  }

  const nextClipsById = {};
  Object.entries(edl.clips.byId).forEach(([clipId, clip]) => {
    nextClipsById[clipId] = hydrateClipDefaults({
      clip,
      compositionWidth: edl.compositionWidth,
      compositionHeight: edl.compositionHeight,
    });
  });

  return {
    ...edl,
    clips: {
      byId: nextClipsById,
    },
  };
};

export const applyClipSourceDimensions = ({
  clip,
  sourceWidth,
  sourceHeight,
  compositionWidth,
  compositionHeight,
}) => {
  if (!clip || !hasSourceDimensions({ sourceWidth, sourceHeight })) {
    return clip;
  }

  if (
    clip.sourceWidth === sourceWidth &&
    clip.sourceHeight === sourceHeight
  ) {
    return clip;
  }

  const nextClip = {
    ...clip,
    sourceWidth,
    sourceHeight,
  };

  if (
    clipHasCanvasLayout(nextClip) &&
    isValidClipLayout(nextClip.layout) &&
    !isFullCanvasClipLayout(nextClip.layout)
  ) {
    nextClip.layout = repairLayoutAspectRatio({
      layout: nextClip.layout,
      sourceWidth,
      sourceHeight,
      compositionWidth,
      compositionHeight,
    });
  }

  const layoutUnchanged =
    nextClip.layout?.x === clip.layout?.x &&
    nextClip.layout?.y === clip.layout?.y &&
    nextClip.layout?.width === clip.layout?.width &&
    nextClip.layout?.height === clip.layout?.height;

  if (
    nextClip.sourceWidth === clip.sourceWidth &&
    nextClip.sourceHeight === clip.sourceHeight &&
    layoutUnchanged
  ) {
    return clip;
  }

  return nextClip;
};

export const applySourceSizesToEdl = ({
  edl,
  sizesBySourceUrl = {},
  compositionSourceUrl,
} = {}) => {
  if (!edl?.clips?.byId) {
    return edl;
  }

  const currentClips = Object.values(edl.clips.byId);
  const mainClip =
    currentClips.find(
      (clip) =>
        clip?.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO &&
        clip.sourceUrl === compositionSourceUrl &&
        isFullCanvasClipLayout(clip.layout)
    ) ||
    currentClips
      .filter(
        (clip) =>
          clip?.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO &&
          isFullCanvasClipLayout(clip.layout)
      )
      .sort(
        (left, right) =>
          (Number(left.position) || 0) - (Number(right.position) || 0)
      )[0];
  const compositionSize = mainClip && sizesBySourceUrl[mainClip.sourceUrl];

  const compositionWidth =
    compositionSize && Number(compositionSize.width) > 0
      ? compositionSize.width
      : edl.compositionWidth;
  const compositionHeight =
    compositionSize && Number(compositionSize.height) > 0
      ? compositionSize.height
      : edl.compositionHeight;

  const nextClipsById = { ...edl.clips.byId };
  let changed =
    Number(compositionWidth) !== Number(edl.compositionWidth) ||
    Number(compositionHeight) !== Number(edl.compositionHeight);

  Object.entries(nextClipsById).forEach(([clipId, clip]) => {
    const size = sizesBySourceUrl[clip?.sourceUrl];
    if (!size) {
      return;
    }
    const nextClip = applyClipSourceDimensions({
      clip,
      sourceWidth: size.width,
      sourceHeight: size.height,
      compositionWidth,
      compositionHeight,
    });
    if (nextClip !== clip) {
      nextClipsById[clipId] = nextClip;
      changed = true;
    }
  });

  if (!changed) {
    return edl;
  }

  return {
    ...edl,
    compositionWidth,
    compositionHeight,
    clips: {
      byId: nextClipsById,
    },
  };
};
